import { NavLink, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import HttpInterceptor from '../lib/HttpInterceptor';
import logo from '../images/logo.png';

function Footer() {
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPWA, setIsPWA] = useState(false);
  
  // Check if user is on a student route
  const isStudentRoute = location.pathname.startsWith('/student/');
  
  // Check if user is on a trainer/consultant route
  const isTrainerRoute = location.pathname.startsWith('/trainer/');
  
  // Check if user is on consultant dashboard (hide marketplace links)
  const isConsultantDashboard = location.pathname === '/trainer/consultant-dashboard' || 
                                 location.pathname.startsWith('/trainer/consultant-dashboard');
  
  // Determine route prefix based on context
  const getRoute = (publicRoute) => {
    if (isStudentRoute) {
      // Map public routes to student routes
      if (publicRoute === '/Market') return '/student/market';
      if (publicRoute === '/help') return '/student/help';
      if (publicRoute === '/reseller-application') return '/student/reseller-application';
      if (publicRoute === '/about') return '/student/about';
      if (publicRoute === '/services') return '/student/services';
    }
    // For trainer/consultant routes, keep public routes as-is since there are no trainer-specific versions
    // They will navigate to public pages which is fine
    return publicRoute;
  };

  // Detect PWA standalone mode
  useEffect(() => {
    const checkPWA = () => {
      const standalone = window.matchMedia('(display-mode: standalone)').matches || 
                         window.navigator.standalone === true;
      setIsPWA(standalone);
    };
    checkPWA();
    window.addEventListener('resize', checkPWA);
    return () => window.removeEventListener('resize', checkPWA);
  }, []);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    
    if (!email.trim()) {
      toast.error('Please enter your email address');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      toast.error('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await HttpInterceptor.post('/api/newsletter/subscribe', {
        email: email.trim(),
      });
      
      toast.success(response.data.message || 'Thank you for subscribing!');
      setEmail(''); // Clear input after successful subscription
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to subscribe. Please try again.';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer className="text-white font-sans">
      {/* Top Section - Social Media Links */}
      {isPWA ? (
        // PWA Mode - Enhanced Design
        <div className="bg-gradient-to-br from-teal-50 via-blue-50 to-indigo-50 py-8 sm:py-10 relative overflow-hidden">
          {/* Decorative background elements */}
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-teal-400/60 to-transparent"></div>
          <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-200/40 to-transparent"></div>
          
          <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="flex flex-col items-center">
              {/* Enhanced Header */}
              <div className="mb-6 sm:mb-8">
                <div className="flex items-center justify-center gap-3 mb-3">
                  <div className="relative">
                    <div className="absolute left-0 w-1.5 h-6 bg-gradient-to-b from-teal-400/40 via-blue-400/40 to-indigo-400/40 rounded-full blur-sm"></div>
                    <div className="relative w-1.5 h-6 bg-gradient-to-b from-teal-500 via-blue-500 to-indigo-500 rounded-full shadow-md"></div>
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold text-teal-900 tracking-wide">
                    CONNECT WITH US
                  </h3>
                  <div className="relative">
                    <div className="absolute left-0 w-1.5 h-6 bg-gradient-to-b from-teal-400/40 via-blue-400/40 to-indigo-400/40 rounded-full blur-sm"></div>
                    <div className="relative w-1.5 h-6 bg-gradient-to-b from-teal-500 via-blue-500 to-indigo-500 rounded-full shadow-md"></div>
                  </div>
                </div>
                <p className="text-xs sm:text-sm text-teal-600/70 text-center">
                  Follow us on social media for updates and inspiration
                </p>
              </div>
              
              {/* Enhanced Social Media Icons */}
              <div className="flex flex-nowrap justify-center gap-2 sm:gap-3 md:gap-4 overflow-x-auto w-full px-2">
                {/* Facebook */}
                <a 
                  href="https://facebook.com/yogvital" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="group bg-gradient-to-br from-white to-blue-50 border-2 border-blue-200/60 hover:border-blue-400 p-2 sm:p-3 md:p-4 rounded-xl sm:rounded-2xl transition-all duration-300 transform hover:scale-110 hover:shadow-xl shadow-lg relative overflow-hidden flex-shrink-0"
                  aria-label="Follow us on Facebook"
                >
                  <div className="absolute top-0 right-0 w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-blue-100/30 to-blue-200/20 rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="relative z-10">
                    <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-1.5 sm:p-2 md:p-3 rounded-lg sm:rounded-xl shadow-md group-hover:scale-110 transition-transform duration-300">
                      <svg className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-white" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z"/>
                      </svg>
                    </div>
                  </div>
                </a>
                
                {/* Instagram */}
                <a 
                  href="https://instagram.com/yogvital" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="group bg-gradient-to-br from-white to-pink-50 border-2 border-pink-200/60 hover:border-pink-400 p-2 sm:p-3 md:p-4 rounded-xl sm:rounded-2xl transition-all duration-300 transform hover:scale-110 hover:shadow-xl shadow-lg relative overflow-hidden flex-shrink-0"
                  aria-label="Follow us on Instagram"
                >
                  <div className="absolute top-0 right-0 w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-pink-100/30 to-purple-100/20 rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="relative z-10">
                    <div className="bg-gradient-to-r from-purple-500 via-pink-500 to-orange-400 p-1.5 sm:p-2 md:p-3 rounded-lg sm:rounded-xl shadow-md group-hover:scale-110 transition-transform duration-300">
                      <svg className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-white" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 3c-2.444 0-2.75.01-3.71.054-.959.044-1.613.196-2.185.418A4.943 4.943 0 0 0 3.472 6.1c-.222.572-.374 1.226-.418 2.184C3.01 9.25 3 9.556 3 12s.01 2.75.054 3.71c.044.959.196 1.613.418 2.185.393.912.914 1.633 1.71 2.185.572.222 1.226.374 2.184.418C9.25 20.99 9.556 21 12 21s2.75-.01 3.71-.054c.959-.044 1.613-.196 2.185-.419a4.908 4.908 0 0 0 1.71-2.184c.222-.572.374-1.226.418-2.184.044-.96.054-1.267.054-3.711s-.01-2.75-.054-3.71c-.044-.959-.196-1.613-.419-2.185a4.943 4.943 0 0 0-2.184-1.71c-.572-.222-1.226-.374-2.185-.418C14.75 3.01 14.444 3 12 3zm0 1.622c2.403 0 2.688.009 3.637.052.877.04 1.354.187 1.671.31.421.163.72.358 1.036.673.315.315.51.615.673 1.036.123.317.27.794.31 1.671.043.949.052 1.234.052 3.637s-.009 2.688-.052 3.637c-.04.877-.187 1.354-.31 1.671-.163.421-.358.72-.673 1.036a2.79 2.79 0 0 1-1.036.673c-.317.123-.794.27-1.671.31-.949.043-1.234.052-3.637.052s-2.688-.009-3.637-.052c-.877-.04-1.354-.187-1.671-.31a2.789 2.789 0 0 1-1.036-.673 2.79 2.79 0 0 1-.673-1.036c-.123-.317-.27-.794-.31-1.671-.043-.949-.052-1.234-.052-3.637s.009-2.688.052-3.637c.04-.877.187-1.354.31-1.671.163-.421.358-.72.673-1.036.315-.315.615-.51 1.036-.673.317-.123.794-.27 1.671-.31.949-.043 1.234-.052 3.637-.052zM12 7a5 5 0 1 0 0 10 5 5 0 0 0 0-10zm0 1.622a3.378 3.378 0 1 1 0 6.756 3.378 3.378 0 0 1 0-6.756zM17.5 7a1.5 1.5 0 1 0-3 0 1.5 1.5 0 0 0 3 0z"/>
                      </svg>
                    </div>
                  </div>
                </a>

                {/* YouTube */}
                <a 
                  href="https://youtube.com/@yogvital" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="group bg-gradient-to-br from-white to-red-50 border-2 border-red-200/60 hover:border-red-400 p-2 sm:p-3 md:p-4 rounded-xl sm:rounded-2xl transition-all duration-300 transform hover:scale-110 hover:shadow-xl shadow-lg relative overflow-hidden flex-shrink-0"
                  aria-label="Subscribe to our YouTube channel"
                >
                  <div className="absolute top-0 right-0 w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-red-100/30 to-red-200/20 rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="relative z-10">
                    <div className="bg-gradient-to-br from-red-500 to-red-600 p-1.5 sm:p-2 md:p-3 rounded-lg sm:rounded-xl shadow-md group-hover:scale-110 transition-transform duration-300">
                      <svg className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-white" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z"/>
                      </svg>
                    </div>
                  </div>
                </a>

                {/* Pinterest */}
                <a 
                  href="https://pinterest.com/yogvital" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="group bg-gradient-to-br from-white to-rose-50 border-2 border-rose-200/60 hover:border-rose-400 p-2 sm:p-3 md:p-4 rounded-xl sm:rounded-2xl transition-all duration-300 transform hover:scale-110 hover:shadow-xl shadow-lg relative overflow-hidden flex-shrink-0"
                  aria-label="Follow us on Pinterest"
                >
                  <div className="absolute top-0 right-0 w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-rose-100/30 to-rose-200/20 rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="relative z-10">
                    <div className="bg-gradient-to-br from-rose-500 to-rose-600 p-1.5 sm:p-2 md:p-3 rounded-lg sm:rounded-xl shadow-md group-hover:scale-110 transition-transform duration-300">
                      <svg className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-white" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 0C5.373 0 0 5.372 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738.098.119.112.224.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.631-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12 0-6.628-5.373-12-12-12z"/>
                      </svg>
                    </div>
                  </div>
                </a>
              </div>
            </div>
          </div>
        </div>
      ) : (
        // Browser Mode - Original Design
        <div className="bg-gradient-to-l from-dark-100 to-primary-100 py-6 shadow-[0_-4px_12px_rgba(0,0,0,0.2)] relative z-10">
          <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col items-center">
              <h3 className="text-xl font-light tracking-wider mb-6 text-black hover:text-white transition-all duration-500">
                CONNECT WITH US
              </h3>
              <div className="flex space-x-5">
                {/* Facebook */}
                <a href="https://facebook.com/yogvital" target="_blank" rel="noopener noreferrer"
                  className="bg-gray-700 p-3 rounded-full hover:bg-blue-600 transition-all duration-300 transform hover:scale-110 shadow-lg hover:shadow-blue-500/30">
                  <svg className="w-6 h-6 text-gray-300 hover:text-white" viewBox="0 0 24 24">
                    <path fill="currentColor" d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z"/>
                  </svg>
                </a>
                {/* Instagram */}
                <a href="https://instagram.com/yogvital" target="_blank" rel="noopener noreferrer"
                  className="bg-gray-700 p-3 rounded-full hover:bg-gradient-to-r from-purple-500 via-pink-500 to-orange-400 transition-all duration-300 transform hover:scale-110 shadow-lg hover:shadow-pink-500/30">
                  <svg className="w-6 h-6 text-gray-300 hover:text-white" viewBox="0 0 24 24">
                    <path fill="currentColor" d="M12 3c-2.444 0-2.75.01-3.71.054-.959.044-1.613.196-2.185.418A4.943 4.943 0 0 0 3.472 6.1c-.222.572-.374 1.226-.418 2.184C3.01 9.25 3 9.556 3 12s.01 2.75.054 3.71c.044.959.196 1.613.418 2.185.393.912.914 1.633 1.71 2.185.572.222 1.226.374 2.184.418C9.25 20.99 9.556 21 12 21s2.75-.01 3.71-.054c.959-.044 1.613-.196 2.185-.419a4.908 4.908 0 0 0 1.71-2.184c.222-.572.374-1.226.418-2.184.044-.96.054-1.267.054-3.711s-.01-2.75-.054-3.71c-.044-.959-.196-1.613-.419-2.185a4.943 4.943 0 0 0-2.184-1.71c-.572-.222-1.226-.374-2.185-.418C14.75 3.01 14.444 3 12 3zm0 1.622c2.403 0 2.688.009 3.637.052.877.04 1.354.187 1.671.31.421.163.72.358 1.036.673.315.315.51.615.673 1.036.123.317.27.794.31 1.671.043.949.052 1.234.052 3.637s-.009 2.688-.052 3.637c-.04.877-.187 1.354-.31 1.671-.163.421-.358.72-.673 1.036a2.79 2.79 0 0 1-1.036.673c-.317.123-.794.27-1.671.31-.949.043-1.234.052-3.637.052s-2.688-.009-3.637-.052c-.877-.04-1.354-.187-1.671-.31a2.789 2.789 0 0 1-1.036-.673 2.79 2.79 0 0 1-.673-1.036c-.123-.317-.27-.794-.31-1.671-.043-.949-.052-1.234-.052-3.637s.009-2.688.052-3.637c.04-.877.187-1.354.31-1.671.163-.421.358-.72.673-1.036.315-.315.615-.51 1.036-.673.317-.123.794-.27 1.671-.31.949-.043 1.234-.052 3.637-.052zM12 7a5 5 0 1 0 0 10 5 5 0 0 0 0-10zm0 1.622a3.378 3.378 0 1 1 0 6.756 3.378 3.378 0 0 1 0-6.756zM17.5 7a1.5 1.5 0 1 0-3 0 1.5 1.5 0 0 0 3 0z"/>
                  </svg>
                </a>

                {/* YouTube */}
                <a href="https://youtube.com/@yogvital" target="_blank" rel="noopener noreferrer"
                  className="bg-gray-700 p-3 rounded-full hover:bg-red-600 transition-all duration-300 transform hover:scale-110 shadow-lg hover:shadow-red-500/30">
                  <svg className="w-6 h-6 text-gray-300 hover:text-white" viewBox="0 0 24 24">
                    <path fill="currentColor" d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z"/>
                  </svg>
                </a>

                {/* Pinterest */}
                <a href="https://pinterest.com/yogvital" target="_blank" rel="noopener noreferrer"
                  className="bg-gray-700 p-3 rounded-full hover:bg-red-500 transition-all duration-300 transform hover:scale-110 shadow-lg hover:shadow-red-500/30">
                  <svg className="w-6 h-6 text-gray-300 hover:text-white" viewBox="0 0 24 24">
                    <path fill="currentColor" d="M12 0C5.373 0 0 5.372 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738.098.119.112.224.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.631-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12 0-6.628-5.373-12-12-12z"/>
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Footer Content */}
      {isPWA ? (
        // PWA Mode - Compact Footer
        <div className="bg-gray-800/95 backdrop-blur-sm py-4 border-t border-gray-700/30">
          <div className="max-w-4xl mx-auto px-4">
            <div className="flex flex-col items-center space-y-4">
              <NavLink to="/" className="transition-all duration-700 hover:opacity-90">
                <img 
                  src={logo} 
                  alt="Logo" 
                  className="h-8 w-auto filter brightness-110 contrast-110 opacity-95"
                />
              </NavLink>
              <div className="flex flex-wrap justify-center gap-4 text-xs">
                {!isConsultantDashboard && (
                  <>
                    <NavLink to={getRoute("/help")} className="text-white/60 hover:text-white transition-all duration-300">
                      Help Center
                    </NavLink>
                    <span className="text-white/40">•</span>
                  </>
                )}
                <NavLink to="/about" className="text-white/60 hover:text-white transition-all duration-300">
                  About
                </NavLink>
                <span className="text-white/40">•</span>
                <NavLink to="/privacy-policy" className="text-white/60 hover:text-white transition-all duration-300">
                  Privacy
                </NavLink>
                <span className="text-white/40">•</span>
                <NavLink to="/terms-of-service" className="text-white/60 hover:text-white transition-all duration-300">
                  Terms
                </NavLink>
              </div>
            </div>
          </div>
        </div>
      ) : (
        // Browser Mode - Full Footer
        <div className="bg-gray-800/95 backdrop-blur-sm py-8 border-t border-gray-700/30">
          <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
              {/* Logo Column */}
              <div className="flex flex-col items-center md:items-start">
                <NavLink to="/" className="mb-4 transition-all duration-700 hover:opacity-90">
                  <img 
                    src={logo} 
                    alt="Logo" 
                    className="h-10 w-auto filter brightness-110 contrast-110 opacity-95"
                  />
                </NavLink>
                <p className="text-white/60 text-sm font-light leading-relaxed text-center md:text-left max-w-[240px]">
                  Crafting digital experiences that inspire and engage 
                  <span className="inline-flex items-center">
                    . With
                    <svg className="w-4 h-4 mx-1 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd" />
                    </svg>
                    by Vatsal Gupta
                  </span>
                </p>
              </div>

              {/* Quick Links - Hidden on mobile */}
              <div className="hidden md:flex flex-col items-center md:items-start">
                <h4 className="text-white/90 text-sm font-medium uppercase tracking-wider mb-4">Quick Links</h4>
                <div className="space-y-3">
                  <NavLink
                    to="/"
                    className={({ isActive }) => 
                      `block text-white/60 hover:text-white text-sm font-light tracking-wider transition-all duration-300
                      ${isActive ? 'text-white' : ''}`
                    }
                  >
                    Home
                  </NavLink>
                  <NavLink
                    to={getRoute("/about")}
                    className={({ isActive }) => 
                      `block text-white/60 hover:text-white text-sm font-light tracking-wider transition-all duration-300
                      ${isActive ? 'text-white' : ''}`
                    }
                  >
                    About
                  </NavLink>
                  {!isConsultantDashboard && (
                    <NavLink
                      to={getRoute("/services")}
                      className={({ isActive }) => 
                        `block text-white/60 hover:text-white text-sm font-light tracking-wider transition-all duration-300
                        ${isActive ? 'text-white' : ''}`
                      }
                    >
                      Services
                    </NavLink>
                  )}
                </div>
              </div>
              
              {/* Marketplace Links - Hidden on mobile and consultant dashboard */}
              {!isConsultantDashboard && (
                <div className="hidden md:flex flex-col items-center md:items-start">
                  <h4 className="text-white/90 text-sm font-medium uppercase tracking-wider mb-4">Marketplace</h4>
                  <div className="space-y-3">
                    <NavLink
                      to={getRoute("/Market")}
                      className={({ isActive }) => 
                        `block text-white/60 hover:text-white text-sm font-light tracking-wider transition-all duration-300
                        ${isActive ? 'text-white' : ''}`
                      }
                    >
                      Shop
                    </NavLink>
                    <NavLink
                      to={getRoute("/reseller-application")}
                      className={({ isActive }) => 
                        `block text-white/60 hover:text-white text-sm font-light tracking-wider transition-all duration-300
                        ${isActive ? 'text-white' : ''}`
                      }
                    >
                      Sell on Platform
                    </NavLink>
                    <NavLink
                      to={getRoute("/help")}
                      className={({ isActive }) => 
                        `block text-white/60 hover:text-white text-sm font-light tracking-wider transition-all duration-300
                        ${isActive ? 'text-white' : ''}`
                      }
                    >
                      Help Center
                    </NavLink>
                  </div>
                </div>
              )}
              {/* Contact Info - Hidden on mobile */}
              <div className="hidden md:flex flex-col items-center md:items-start">
                <h4 className="text-white/90 text-sm font-medium uppercase tracking-wider mb-4">Contact Us</h4>
                <div className="space-y-3 text-white/60 text-sm font-light">
                  <p>contact@yogvital.com</p>
                  <p>+91 89-2316-2316</p>
                </div>
              </div>

              {/* Newsletter - Hidden on mobile to match other sections */}
              <div className="hidden md:flex flex-col items-center md:items-start">
                <h4 className="text-white/90 text-sm font-medium uppercase tracking-wider mb-4">Newsletter</h4>
                <p className="text-white/60 text-sm font-light mb-4">
                  Subscribe for the latest updates.
                </p>
                <form onSubmit={handleNewsletterSubmit} className="flex w-full">
                  <input 
                    type="email" 
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Your email" 
                    disabled={isSubmitting}
                    className="bg-white/10 border border-white/20 text-white/80 placeholder-white/40 px-4 py-2 text-sm focus:outline-none focus:border-white/40 transition-all duration-300 w-full disabled:opacity-50 disabled:cursor-not-allowed"
                    required
                  />
                  <button 
                    type="submit"
                    disabled={isSubmitting}
                    className="bg-white/10 hover:bg-white/20 border border-white/20 text-white px-4 py-2 text-sm transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center min-w-[40px]"
                    aria-label="Subscribe to newsletter"
                  >
                    {isSubmitting ? (
                      <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    ) : (
                      '→'
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Copyright Section */}
      {isPWA ? (
        // PWA Mode - Compact Copyright
        <div className="bg-gray-900 py-2 border-t border-gray-700/30">
          <div className="max-w-4xl mx-auto px-4">
            <p className="text-white/50 text-xs font-light text-center">
              © {new Date().getFullYear()} Yogvital
            </p>
          </div>
        </div>
      ) : (
        // Browser Mode - Full Copyright
        <div className="bg-gray-900 py-4 border-t border-gray-700/30">
          <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8 md:pr-48">
            <div className="flex flex-col md:flex-row justify-center md:justify-between items-center">
              <p className="text-white/50 text-xs font-light tracking-wider mb-3 md:mb-0 text-center md:text-left">
                © {new Date().getFullYear()} Yogvital. All rights reserved.
              </p>
              <div className="flex flex-wrap gap-4 md:gap-6 justify-center md:justify-start">
                {!isConsultantDashboard && (
                  <NavLink to={getRoute("/help")} className="text-white/50 hover:text-white text-xs font-light transition-all duration-300 md:hidden">Help Center</NavLink>
                )}
                <NavLink to="/privacy-policy" className="text-white/50 hover:text-white text-xs font-light transition-all duration-300">Privacy Policy</NavLink>
                <NavLink to="/terms-of-service" className="text-white/50 hover:text-white text-xs font-light transition-all duration-300">Terms of Service</NavLink>
              </div>
            </div>
          </div>
        </div>
      )}
    </footer>
  );
}

export default Footer;